/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect } from "react";
import FormFader from "../Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import Hidden from "@mui/material/Hidden";
import EnforceIcon from "../../../assets/icons/contract/toggleable/IconEnforce";
import DefendIcon from "../../../assets/icons/contract/toggleable/IconDefend";
import AdvisoryIcon from "../../../assets/icons/contract/toggleable/IconQuestionmarkBubble";
import Person from "../Common/Person/Person";
import _includes from "lodash/includes";
import Address from "../Common/Address/Address";
import PersonIcon from "../../../assets/icons/provision/toggleable/IconPerson";
import FamilyIcon from "../../../assets/icons/generic/toggleable/IconFamilyDetailed";
import ContractBaseEnforceClaim from "./ContractBaseEnforceClaim";
import ContractBaseDefendClaim from "./ContractBaseDefendClaim";
import { dateFieldDefault, isPhoneNumber, minStringLength, pastOrSameDateDefault, requiredValidator } from "../../../services/validationRules";
import { useForm } from "../../../provider/Form/FormProvider";
import Group from "../../Wizard/Group";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import ValidatorDateField from "../../FormFields/ValidatorDateField";
import AdditionalClientsForm from "../Common/AdditionalClientsForm/AdditionalClientsForm";
import wizardStyle from "../wizardStyle";
import { Box } from "@mui/material";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { translate } from "../../../services/translations/translations";

type RentalContractBaseProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  hideCaseCategories: boolean;
  alternativeHeadline?: string;
};
export default function RentalContractBase({ submitProps, product, hideCaseCategories = false, alternativeHeadline }: RentalContractBaseProps) {
  const { values, handleSubmit, handleChange } = useForm();

  useEffect(() => {
    if (values.legalRepresentativeType === "me") {
      handleChange({
        target: {
          name: "clientLegalRepresentative.gender",
          value: values.customer.person.gender,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.givenName",
          value: values.customer.person.givenName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.familyName",
          value: values.customer.person.familyName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.streetAddress",
          value: values.customer.person.residenceAddress.streetAddress,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.postalCode",
          value: values.customer.person.residenceAddress.postalCode,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.addressLocality",
          value: values.customer.person.residenceAddress.addressLocality,
          type: "text",
        },
      });
      return;
    }
    handleChange({
      target: {
        name: "clientLegalRepresentative.gender",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.givenName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.familyName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.streetAddress",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.postalCode",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.addressLocality",
        value: "",
        type: "text",
      },
    });
  }, [values.legalRepresentativeType]);

  useEffect(() => {
    if (values.affectedPartyType === "coinsured") {
      handleChange({
        target: {
          name: "client.gender",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.givenName",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.familyName",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.birthDate",
          value: null,
        },
      });
    }
  }, [values.affectedPartyType]);

  const getCaseCategoryOptions = () => {
    let options = [
      {
        labelText: translate("rentalContract.caseCategory.options.enforceClaim.label", values),
        labelIcon: <EnforceIcon />,
        labelIconActive: <EnforceIcon active />,
        value: "enforceClaim",
        optionHintText: translate("rentalContract.caseCategory.options.enforceClaim.optionHintText", values),
      },
      {
        labelText: translate("rentalContract.caseCategory.options.defendClaim.label", values),
        labelIcon: <DefendIcon />,
        labelIconActive: <DefendIcon active />,
        value: "defendClaim",
        optionHintText: translate("rentalContract.caseCategory.options.defendClaim.optionHintText", values),
      },
    ];
    if (product.categoryChange === null || product.categoryChange.previousCaseCategory !== "advisory") {
      options = [
        {
          labelText: translate("rentalContract.caseCategory.options.advisory.label", values),
          labelIcon: <AdvisoryIcon />,
          labelIconActive: <AdvisoryIcon active />,
          value: "advisory",
          optionHintText: translate("rentalContract.caseCategory.options.advisory.optionHintText", values),
        },
        ...options,
      ];
    }
    return options;
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Hidden smUp>
            <Typography variant={"h4"} sx={wizardStyle.headline}>
              Ihr Vertrag
            </Typography>
          </Hidden>
          <Grid container justifyContent={"flex-start"} alignItems={"baseline"}>
            <Grid item xs={12}>
              <FormLegend>
                <strong>{alternativeHeadline || "Allgemeines zu Ihrem Mietvertrag"}</strong>
              </FormLegend>
            </Grid>
            {!hideCaseCategories && (
              <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                <Group
                  type="radio"
                  name="caseCategory"
                  question={translate("rentalContract.caseCategory.question", values)}
                  options={getCaseCategoryOptions()}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            )}
            <Person path="customer.person" headline={"Wie heißen Sie?"} showDiverseGenderOption />
            {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && (
              <Address path="customer.person.residenceAddress" headline={"Wie lautet Ihre aktuelle Anschrift? "} />
            )}
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <FormLegend>Wie können wir Sie erreichen?</FormLegend>
            </Grid>
            <Grid item xs={12}>
              <ValidatorTextField
                name="customer.telephone"
                label="Ihre Telefonnummer"
                autoComplete="tel"
                validators={[
                  requiredValidator("Bitte geben Sie Ihre Telefonnummer an"),
                  {
                    validator: minStringLength,
                    params: [7],
                    message: "Ihre Telefonnummer ist zu kurz",
                  },
                  { validator: isPhoneNumber, message: "Ihre Telefonnummer enthält ungültige Zeichen" },
                ]}
              />
            </Grid>
            {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && (
              <>
                <Grid item xs={12}>
                  <Group
                    type="radio"
                    name="affectedPartyType"
                    question={"Wer hat den Mietvertrag abgeschlossen?"}
                    options={[
                      {
                        labelText: "Ich habe den Vertrag selbst abgeschlossen",
                        labelIcon: <PersonIcon />,
                        labelIconActive: <PersonIcon active />,
                        value: "customer",
                      },
                      {
                        labelText: "Der Vertrag wurde von jemand anderem abgeschlossen, der mir bekannt ist - zum Beispiel ein Familienmitglied",
                        labelIcon: <FamilyIcon />,
                        labelIconActive: <FamilyIcon active />,
                        value: "coinsured",
                      },
                    ]}
                    validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                  />
                </Grid>
                {values.affectedPartyType === "customer" && (
                  <>
                    <AdditionalClientsForm allowThreeOrMoreClients={true} product={product} />
                  </>
                )}
                {values.affectedPartyType === "coinsured" && (
                  <>
                    <Person path="client" headline="Wie heißt die Person, die den Vertrag geschlossen hat?" showDiverseGenderOption />
                    <Address path="client.residenceAddress" headline={"Wo wohnt diese Person?"} />
                  </>
                )}
                <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                  <FormLegend>
                    {values.contractPartyType === "tenant"
                      ? "Wann wurde der Mietvertrag unterzeichnet? Prüfen Sie hierzu bitte Ihren Mietvertrag. WICHTIG: Es geht hier nicht um den Beginn des Mietverhältnisses, sondern um das Datum, an dem Sie den Vertrag unterzeichnet haben."
                      : "Wann haben beide Parteien den Mietvertrag unterzeichnet? WICHTIG: Es geht hier nicht um den Beginn des Mietverhältnisses, sondern um das Unterschriftsdatum. Wenn die Unterschriften zu verschiedenen Daten vorgenommen wurden, tragen Sie hier bitte das zeitlich spätere Datum ein."}
                  </FormLegend>
                </Grid>
                {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && (
                  <Grid item xs={12}>
                    <ValidatorDateField
                      name="contractDate"
                      label={translate("rentalContract.contractDate.label", values)}
                      validators={[requiredValidator("Bitte geben Sie das Vertragsdatum an"), dateFieldDefault, pastOrSameDateDefault]}
                      helperText={translate("rentalContract.contractDate.helperText", values)}
                    />
                  </Grid>
                )}
              </>
            )}
            {values.caseCategory === "enforceClaim" && <ContractBaseEnforceClaim />}
            {values.caseCategory === "defendClaim" && <ContractBaseDefendClaim />}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
